import { Box, Divider, Flex, Select } from '@byecode/ui'
import type { ControlActionParams, IndependentAction } from '@lighthouse/core'
import { ActionControlType, type BlockAbstract, type ContainerBlockAbstract } from '@lighthouse/core'
import {
    emptyNumberField,
    type FlowNode,
    getAllCanBeUpstreamNodes,
    getInputInnerTypeMatch,
    getInputVariableOption,
    getIsFindUseInRichText,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    useAtomAction,
    useAtomData,
    useFindUseObjectContext,
    VariableSelect
} from '@lighthouse/shared'
import React, { useCallback, useDeferredValue, useMemo } from 'react'

import { availableFieldBlocksAtom, blockHighlightAtom, lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { SwitchControl } from '@/components/BlockSettings/Common/SwitchControl'
import { SizeInput } from '@/components/StyleSetting/SizeInput'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import * as SC from '../../styles'

const viewControlActionTypeOptions = [
    { label: '刷新', value: 'refresh' },
    { label: '切换分页', value: 'paginateTo' },
    { label: '横向滚动', value: 'horizontalScrollTo' }
]

const viewHorizontalScrollDirectionOptions = [
    { label: '向左', value: 'left' },
    { label: '向右', value: 'right' }
]

const viewControlActionParamsOptions = [
    { label: '上一页', value: 'prev', actionType: 'paginateTo' },
    { label: '下一页', value: 'next', actionType: 'paginateTo' },
    { label: '1', value: '1', actionType: 'horizontalScrollTo' },
    { label: '2', value: '2', actionType: 'horizontalScrollTo' },
    { label: '3', value: '3', actionType: 'horizontalScrollTo' },
    { label: '4', value: '4', actionType: 'horizontalScrollTo' },
    { label: '5', value: '5', actionType: 'horizontalScrollTo' },
    { label: '滚动到底', value: 'end', actionType: 'horizontalScrollTo' }
]

const containerControlActionTypeOptions = [
    { label: '切换面板', value: 'switchPanel' },
    { label: '展开容器', value: 'open' },
    { label: '收起容器', value: 'close' }
]

const fieldControlActionTypeOptions = [{ label: '清空', value: 'clear' }]

const fieldInputControlActionTypeOptions = [...fieldControlActionTypeOptions, { label: '聚焦', value: 'focus' }]

const supportFocusInputType = new Set(['text', 'phoneNumber', 'email', 'url', 'number'])

const containerControlActionDefaultParamsOptions = [
    { label: '下一个面板', value: 'nextPanel', actionType: 'switchPanel', hidden: true },
    { label: '上一个面板', value: 'prevPanel', actionType: 'switchPanel', hidden: true },
    { label: '第几个面板', value: 'nthPanel', actionType: 'switchPanel', hidden: true }
]

const isLoopSwitchPanel = (switchPanelId: string) => {
    return switchPanelId === 'nextPanel' || switchPanelId === 'prevPanel'
}

interface ControlActionSelectorProps {
    controlElement: BlockAbstract
    value?: ControlActionParams
    onChange: (value?: ControlActionParams) => void
    allParentActions?: IndependentAction[]
    allParentNodes?: FlowNode[]
}

export const ControlActionSelector: React.FC<ControlActionSelectorProps> = ({
    controlElement,
    value,
    onChange,
    allParentActions,
    allParentNodes
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const findUseObject = useFindUseObjectContext()
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()
    const { customViewOption } = useVariableCustomViewOption()
    const controlActionType = value?.type
    const switchPanelId = value?.type === 'switchPanel' ? value.params?.switchPanelId : undefined
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )
    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const inputOption = useMemo(() => {
        if (availableFieldBlocksInfo.length === 0) {
            return undefined
        }
        return getInputVariableOption(availableFieldBlocksInfo, dataSourceList, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [availableFieldBlocksInfo, dataSourceList, handleMouseEnter, handleMouseLeave])

    const containerControlActionParamsOptions = useMemo(() => {
        const containerBlock = controlElement as ContainerBlockAbstract
        if (!containerBlock?.config || containerBlock?.type !== 'container') {
            return []
        }

        const options = containerBlock.config.viewList.map(view => ({
            label: view.name,
            value: view.id,
            actionType: 'switchPanel'
        }))

        return [...options, ...containerControlActionDefaultParamsOptions]
    }, [controlElement])

    /**
     * 根据控件类型返回不同的执行行为类型选项
     */
    const controlActionTypeOptions = useMemo(() => {
        if (controlElement?.type === 'container') {
            return containerControlActionTypeOptions
        }

        /** */
        if (controlElement?.type === 'view') {
            const isWrappedHorizontalGrid =
                controlElement.config.viewType === 'custom' &&
                controlElement.config.breakPoint?.viewLayout?.layout === 'grid' &&
                controlElement.config.breakPoint?.viewLayout?.direction === 'horizontal' &&
                controlElement.config.breakPoint?.viewLayout?.wrap === true
            const options: typeof viewControlActionTypeOptions = [{ label: '刷新', value: 'refresh' }]
            // 如果是看板或者日历视图，且方向为垂直，则不显示切换分页
            if (
                !isWrappedHorizontalGrid &&
                !(
                    ['kanban', 'calendar'].includes(controlElement?.config?.viewType) ||
                    (['custom', 'gallery'].includes(controlElement?.config?.viewType) &&
                        controlElement?.config?.breakPoint?.viewLayout?.direction === 'horizontal')
                )
            ) {
                options.push({ label: '切换分页', value: 'paginateTo' }, { label: '加载更多记录', value: 'loadMore' })
            }
            if (
                ['custom', 'gallery'].includes(controlElement?.config?.viewType) &&
                controlElement?.config?.breakPoint?.viewLayout?.direction === 'horizontal' &&
                !isWrappedHorizontalGrid
            ) {
                options.push({ label: '横向滚动', value: 'horizontalScrollTo' })
            }

            options.push({ label: '滚动到某行记录', value: 'scrollToRecord' })
            return options
        }

        if (controlElement?.type === 'field') {
            if (supportFocusInputType.has(controlElement.config.inputType)) {
                return fieldInputControlActionTypeOptions
            }
            return fieldControlActionTypeOptions
        }

        return []
    }, [controlElement])

    const canHorizontalScroll = useMemo(() => {
        const isWrappedHorizontalGrid =
            controlElement?.type === 'view' &&
            controlElement.config.viewType === 'custom' &&
            controlElement.config.breakPoint?.viewLayout?.layout === 'grid' &&
            controlElement.config.breakPoint?.viewLayout?.direction === 'horizontal' &&
            controlElement.config.breakPoint?.viewLayout?.wrap === true

        return (
            controlElement?.type === 'view' &&
            controlActionType === 'horizontalScrollTo' &&
            (controlElement?.config?.viewType === 'custom' || controlElement?.config?.viewType === 'gallery') &&
            !isWrappedHorizontalGrid
        )
    }, [controlActionType, controlElement?.config, controlElement?.type])

    const paramsSelectorLabel = useMemo(() => {
        if (controlActionType === 'paginateTo') {
            return '切换到'
        }

        if (controlActionType === 'horizontalScrollTo') {
            return '滚动几个卡片'
        }

        if (controlActionType === 'switchPanel') {
            return '切换到面板'
        }

        return ''
    }, [controlActionType])

    const paramsSelectorName = useMemo(() => {
        if (controlActionType === 'paginateTo') {
            return 'paginateToType'
        }

        if (controlActionType === 'horizontalScrollTo') {
            return 'horizontalScrollTo'
        }

        if (controlActionType === 'switchPanel') {
            return 'switchPanelId'
        }
    }, [controlActionType])
    const paramsSelectorNamePath = useMemo(() => `params.${paramsSelectorName}`, [paramsSelectorName])

    /**
     * 根据控件类型返回不同的执行行为参数选项
     */
    const controlActionParamsOptions = useMemo(() => {
        if (controlElement?.type === 'container') {
            return containerControlActionParamsOptions.filter(option => option.actionType === controlActionType)
        }

        return viewControlActionParamsOptions.filter(option => option.actionType === controlActionType)
    }, [containerControlActionParamsOptions, controlActionType, controlElement?.type])

    const { type: flowContainerType } = useFlowContainerContext()
    const isInFlow = !!flowContainerType
    const workflowList = useAtomData(workflowListAtom)

    const upstreamOptions = useMemo(() => {
        if (isInFlow) {
            return getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes || []),
                allUpstreamNodes: allParentNodes,
                dataSourceList
            })
        }
        return getUpstreamActionsOptions({
            workflowList,
            allParentActions: allParentActions || [],
            dataSourceList
        })
    }, [allParentActions, allParentNodes, dataSourceList, isInFlow, workflowList])

    const containerControlActionParamsExtra = useCallback(
        (onSelect?: (value: string) => void) => {
            if (controlActionType !== 'switchPanel') {
                return
            }

            return (
                <>
                    <Divider my={8} />
                    {containerControlActionDefaultParamsOptions.map(item => {
                        return (
                            <SC.OptionItem key={item.value} onClick={() => onSelect?.(item.value)}>
                                <Flex gap="8px" alignItems="center" justifyContent="space-between">
                                    <Flex gap="8px" styles={{ root: { lineHeight: '16px', overflow: 'hidden' } }}>
                                        <Box>{item.label}</Box>
                                    </Flex>
                                </Flex>
                            </SC.OptionItem>
                        )
                    })}
                </>
            )
        },
        [controlActionType]
    )

    return (
        <>
            <ListItem4ByecodeUi>
                <SC.FormItemLabelWrapper>
                    <SC.FormItemLabel required>执行行为</SC.FormItemLabel>
                </SC.FormItemLabelWrapper>
                <SC.FormItemContent>
                    <Select
                        options={controlActionTypeOptions}
                        value={controlActionType}
                        onChange={nextType => {
                            const t = nextType as ControlActionParams['type']
                            const emptyScrollToRecord = undefined as unknown as Extract<
                                ControlActionParams,
                                { type: 'scrollToRecord' }
                            >['params']['scrollToRecord']

                            if (t === 'paginateTo') {
                                onChange({ type: ActionControlType.PAGINATE_TO, params: { paginateToType: 'prev' } })
                                return
                            }
                            if (t === 'horizontalScrollTo') {
                                onChange({
                                    type: ActionControlType.HORIZONTAL_SCROLL_TO,
                                    params: { horizontalScrollDirection: 'left', horizontalScrollTo: 1 }
                                })
                                return
                            }
                            if (t === 'switchPanel') {
                                onChange({ type: ActionControlType.SWITCH_PANEL, params: { switchPanelId: '' } })
                                return
                            }
                            if (t === 'scrollToRecord') {
                                onChange({ type: ActionControlType.SCROLL_TO_RECORD, params: { scrollToRecord: emptyScrollToRecord } })
                                return
                            }
                            onChange({ type: t })
                        }}
                    />
                </SC.FormItemContent>
            </ListItem4ByecodeUi>

            {controlElement?.type === 'view' && controlActionType === 'scrollToRecord' && (
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>记录编号</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {(() => {
                            if (value?.type !== 'scrollToRecord') {
                                return null
                            }
                            const doc = value.params.scrollToRecord
                            const isHighLight = getIsFindUseInRichText({ doc, findUseObject })
                            return (
                                <NodeFieldPreview
                                    innerTypes={getInputInnerTypeMatch('TEXT')}
                                    highlighting={isHighLight}
                                    style={{ width: 180 }}
                                    autoHeight
                                    viewOptions={customViewOption ? [customViewOption] : undefined}
                                    pageOptions={pageOptions}
                                    userOption={userOption}
                                    value={doc}
                                    dataSourceList={dataSourceList}
                                    onBlur={scrollToRecord => onChange({ ...value, params: { ...value.params, scrollToRecord } })}
                                />
                            )
                        })()}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            )}

            {canHorizontalScroll && (
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>滚动方向</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <Select
                            options={viewHorizontalScrollDirectionOptions}
                            value={value?.type === 'horizontalScrollTo' ? value.params.horizontalScrollDirection : undefined}
                            onChange={val => {
                                if (value?.type !== 'horizontalScrollTo') {
                                    return
                                }
                                onChange({ ...value, params: { ...value.params, horizontalScrollDirection: val as 'left' | 'right' } })
                            }}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            )}
            {paramsSelectorLabel && (
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>{paramsSelectorLabel}</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <Select
                            options={controlActionParamsOptions}
                            value={(() => {
                                if (!value) {
                                    return undefined
                                }
                                if (controlActionType === 'paginateTo' && value.type === 'paginateTo') {
                                    return value.params.paginateToType
                                }
                                if (controlActionType === 'horizontalScrollTo' && value.type === 'horizontalScrollTo') {
                                    return String(value.params.horizontalScrollTo)
                                }
                                if (controlActionType === 'switchPanel' && value.type === 'switchPanel') {
                                    return value.params.switchPanelId
                                }
                                return undefined
                            })()}
                            onChange={v => {
                                if (!value) {
                                    return
                                }
                                if (controlActionType === 'paginateTo' && value.type === 'paginateTo') {
                                    onChange({ ...value, params: { ...value.params, paginateToType: v as 'next' | 'prev' } })
                                    return
                                }
                                if (controlActionType === 'horizontalScrollTo' && value.type === 'horizontalScrollTo') {
                                    const selected = v
                                    const horizontalScrollTo = selected === 'end' ? 'end' : Number(selected)
                                    onChange({ ...value, params: { ...value.params, horizontalScrollTo } })
                                    return
                                }
                                if (controlActionType === 'switchPanel' && value.type === 'switchPanel') {
                                    onChange({ ...value, params: { ...value.params, switchPanelId: v } })
                                }
                            }}
                            dropdownProps={{
                                extra: containerControlActionParamsExtra((selected: string) => {
                                    if (!value) {
                                        return
                                    }
                                    if (controlActionType === 'switchPanel' && value.type === 'switchPanel') {
                                        onChange({ ...value, params: { ...value.params, switchPanelId: selected } })
                                    }
                                })
                            }}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            )}

            {paramsSelectorName === 'switchPanelId' && switchPanelId && switchPanelId === 'nthPanel' && (
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>第几个面板</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {/* <SizeInput
                            suffix="个"
                            onChange={nth => {
                                if (value?.type !== 'switchPanel') {
                                    return
                                }
                                onChange({ ...value, params: { ...value.params, nthPanel: Number(nth) } })
                            }}
                            value={value?.type === 'switchPanel' ? value.params.nthPanel : undefined}
                        /> */}
                        <VariableSelect
                            innerTypes={[emptyNumberField.innerType]}
                            value={value?.type === 'switchPanel' ? value.params.nthPanel : undefined}
                            onChange={nth => {
                                if (value?.type !== 'switchPanel') {
                                    return
                                }
                                onChange({ ...value, params: { ...value.params, nthPanel: nth } })
                            }}
                            viewOptions={customViewOption ? [customViewOption] : undefined}
                            inputOption={inputOption}
                            pageOptions={pageOptions}
                            userOption={userOption}
                            upstreamOptions={upstreamOptions}
                            dataSourceList={dataSourceList}
                            field={emptyNumberField}
                            placeholder="序号，例如 1、2、3"
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            )}

            {paramsSelectorName === 'switchPanelId' && switchPanelId && isLoopSwitchPanel(switchPanelId) && (
                <>
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel>{switchPanelId === 'nextPanel' ? '自动向前循环' : '自动向后循环'}</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <SwitchControl
                                checked={Boolean(value?.type === 'switchPanel' ? value.params.autoForwardLoop : false)}
                                onChange={e => {
                                    if (value?.type !== 'switchPanel') {
                                        return
                                    }
                                    onChange({ ...value, params: { ...value.params, autoForwardLoop: e.currentTarget.checked } })
                                }}
                            />
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel>循环间隔</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>
                            <SizeInput
                                suffix="ms"
                                onChange={loopInterval => {
                                    if (value?.type !== 'switchPanel') {
                                        return
                                    }
                                    onChange({ ...value, params: { ...value.params, loopInterval } })
                                }}
                                value={value?.type === 'switchPanel' ? value.params.loopInterval : undefined}
                                min={500}
                                max={60000}
                            />
                        </SC.FormItemContent>
                    </ListItem4ByecodeUi>
                </>
            )}
        </>
    )
}
